// components/dashboard/WildTab.jsx
// Jobs in the Wild — matches the tal.club layout:
//   masthead → hero → talboss nudge → filters → feed + right-rail → fab
import { useEffect, useMemo, useRef, useState } from "react"
import { ArrowLeft, ArrowUpRight } from "lucide-react"
import toast from "react-hot-toast"
import iconWild from "../../assets/wild.png"

// Mask-based icon so it picks up `currentColor` (matches Sidebar.NavImgIcon)
function WildMaskIcon({ size = 20, color = "currentColor" }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size,
      background: color,
      WebkitMaskImage: `url(${iconWild})`, maskImage: `url(${iconWild})`,
      WebkitMaskSize: "contain", maskSize: "contain",
      WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
      WebkitMaskPosition: "center", maskPosition: "center",
      flexShrink: 0,
    }} />
  )
}

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
export const API_BASE = "https://jobscanner-e1dd4.ue.r.appspot.com"
const POSTS_CACHE_KEY = "wild_posts_cache_v1"

// ── Palette (aligned with Home tab: whites + zinc + warm accents) ────────
const W = {
  bg:        "#F7F7F8",
  cardBg:    "#FFFFFF",
  frameBg:   "#FAFAFA",
  border:    "#E4E4E7",
  divider:   "#F0F0F2",
  peach:     "#F4F4F5",
  orange:    "#18181b",   // (legacy name — references map to brand/ink)
  orangeDk:  "#000000",
  cta:       "#F25A20",   // brand accent — CTAs and highlights
  ctaDk:     "#D94712",
  ink:       "#18181b",
  ink2:      "#27272a",
  inkMuted:  "#52525b",
  inkDim:    "#71717a",
  inkFaint:  "#a1a1aa",
  chipBg:    "#FFFFFF",
  chipBorder:"#E4E4E7",
  chipActiveBg:   "#FFF5EC",
  chipActiveFg:   "#9A3A14",
  chipActiveBorder: "#F7D9BE",
  rail:      "#FAFAFA",
  linkedin:  "#0A66C2",
  xBlack:    "#111111",
  live:      "#17A34A",

  // Soft accents (surfaces + borders + ink)
  peachSoft:    "#FFF5EC",
  peachBorder:  "#F7D9BE",
  peachInk:     "#9A3A14",
  greenSoft:    "#ECFDF3",
  greenBorder:  "#C7EDD4",
  greenInk:     "#166534",
  blueSoft:     "#EEF4FF",
  blueBorder:   "#D5E0FF",
  blueInk:      "#1D4ED8",
  violetSoft:   "#F5F0FF",
  violetBorder: "#E3D8FA",
  violetInk:    "#6D28D9",
}

// Max width shared by feed cards & detail body so content doesn't sprawl.
const CARD_MAX = 740

// ── Helpers ────────────────────────────────────────────────────────────────
export function formatAgo(iso) {
  if (!iso) return ""
  const t = new Date(iso).getTime()
  if (!t || isNaN(t)) return ""
  const diffMs = Date.now() - t
  const s = Math.floor(diffMs / 1000)
  if (s < 60) return "just now"
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d ago`
  const w = Math.floor(d / 7)
  if (w < 4) return `${w}w ago`
  const mo = Math.floor(d / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(d / 365)}y ago`
}

function initialsOf(name = "") {
  return name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w[0]?.toUpperCase() || "").join("") || "?"
}

// Deterministic 6-char hex id used to anonymize commenters on the detail
// page — matches tal.club's `anon #xxxxxx` scheme and doubles as that
// commenter's accent color.
function anonHex(seed = "") {
  const s = String(seed || "guest")
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0
  }
  return h.toString(16).padStart(6, "0").slice(0, 6)
}

function sourceOf(url = "") {
  try {
    const host = new URL(url).host.toLowerCase()
    if (host.includes("linkedin")) return "linkedin"
    if (host.includes("twitter") || host.includes("x.com")) return "x"
  } catch {}
  return "self"
}

function sourceLabel(src) {
  if (src === "linkedin") return "LinkedIn"
  if (src === "x") return "X"
  return "the original source"
}

const ROLE_FAMILIES = new Set([
  "engineering","product","design","founders-office","ops","business",
  "content","finance","growth","marketing","ai-ml","data","sales",
])
const SENIORITY_LEVELS = new Set([
  "intern","junior","mid","senior","lead","staff","principal","cxo",
])
const REMOTE_TAGS = new Set(["remote","hybrid","in-office"])

export function classifyPost(p) {
  const author = p.author || {}
  const tagsRaw = Array.isArray(p.tags) ? p.tags.map(t => String(t).trim()) : []
  let role = ""
  let seniority = ""
  const other = []
  for (const t of tagsRaw) {
    const lower = t.toLowerCase()
    if (!role && ROLE_FAMILIES.has(lower)) { role = lower; continue }
    if (!seniority && SENIORITY_LEVELS.has(lower)) { seniority = lower; continue }
    other.push(t)
  }
  const remote = other.find(t => REMOTE_TAGS.has(t.toLowerCase())) || ""
  const otherNoRemote = other.filter(t => !REMOTE_TAGS.has(t.toLowerCase()))

  // Split role string "SDET at Zluri" → chipRole + chipCompany
  const roleStr = p.role || ""
  const splitIdx = roleStr.search(/\s+(?:at|@)\s+/i)
  const chipRole = splitIdx >= 0 ? roleStr.slice(0, splitIdx).trim() : roleStr.trim()
  const chipCompany = splitIdx >= 0
    ? roleStr.slice(splitIdx).replace(/^\s+(?:at|@)\s+/i, "").trim()
    : ""

  return {
    id: p.id || "",
    bossId: p.bossId || null,
    author: {
      name: author.name || "",
      headline: author.headline || "",
      avatar: author.avatar || "",
    },
    role: roleStr,
    chipRole,
    chipCompany,
    summary: p.summary || "",
    body: p.body || "",
    externalUrl: p.externalUrl || "",
    source: sourceOf(p.externalUrl || ""),
    createdAt: p.createdAt || null,
    votes: Number.isFinite(p.votes) ? p.votes : 0,
    commentCount: Number.isFinite(p.commentCount) ? p.commentCount : 0,
    tags: {
      role,                           // engineering, product…
      seniority,                      // junior/mid/senior…
      remote,                         // in-office/remote/hybrid
      other: otherNoRemote,           // location, skills, misc
    },
  }
}

export function normalizeComment(c) {
  const author = c.author || {}
  return {
    id: c.id || "",
    authorId: author.id || "",
    authorName: author.name || "",
    authorByline: author.byline || "",
    authorAvatar: author.avatar || "",
    body: c.body || "",
    createdAt: c.createdAt || null,
  }
}

// ── Avatar w/ source dot ──────────────────────────────────────────────────
function Avatar({ url, name, size = 42, source }) {
  const [failed, setFailed] = useState(false)
  const show = !!url && !failed
  const dotColor =
    source === "x" ? W.xBlack :
    source === "self" ? W.live : W.linkedin
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        background: W.peach,
        border: `1.5px solid ${W.border}`,
        overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {show ? (
          <img
            src={url} alt=""
            referrerPolicy="no-referrer"
            onError={() => setFailed(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span style={{ fontSize: size * 0.3, fontWeight: 800, color: W.ink }}>
            {initialsOf(name)}
          </span>
        )}
      </div>
      {source && (
        <span style={{
          position: "absolute", right: -1, bottom: 1,
          width: 12, height: 12, borderRadius: "50%",
          background: dotColor, border: `2px solid ${W.cardBg}`,
        }} title={sourceLabel(source)} aria-label={sourceLabel(source)} />
      )}
    </div>
  )
}

// ── Role chip (role @ company) ────────────────────────────────────────────
function RoleChip({ role, company }) {
  if (!role) return null
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 12px", borderRadius: 999,
      background: W.peachSoft, color: W.peachInk,
      border: `1px solid ${W.peachBorder}`,
      fontSize: 12, fontWeight: 700, letterSpacing: 0.1, fontFamily: FF,
      lineHeight: 1.2,
    }}>
      <span>{role}</span>
      {company && (
        <>
          <span style={{ opacity: 0.6, fontWeight: 500 }}>@</span>
          <span style={{ color: W.ink, fontWeight: 800 }}>{company}</span>
        </>
      )}
    </span>
  )
}

// ── Tag strip ─────────────────────────────────────────────────────────────
function TagStrip({ tags }) {
  const chips = []
  // Neutral outline chips — one shared visual, matches Home job cards
  if (tags.role) chips.push({ k: "role", t: tags.role, bg: "#F4F4F5", fg: "#18181b", strong: true })
  if (tags.seniority) chips.push({ k: "sen", t: tags.seniority })
  if (tags.remote) chips.push({ k: "rem", t: tags.remote })
  for (const o of tags.other) chips.push({ k: "o", t: o })
  if (chips.length === 0) return null
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
      {chips.map((c, i) => (
        <span key={`${c.k}-${i}`} style={{
          padding: "3px 10px", borderRadius: 999,
          background: c.bg || W.cardBg,
          color: c.fg || W.inkMuted,
          border: `1px solid ${c.strong ? "transparent" : W.border}`,
          fontSize: 11, fontWeight: c.strong ? 700 : 600, letterSpacing: 0.1,
          fontFamily: FF, whiteSpace: "nowrap", lineHeight: 1.5,
        }}>
          {c.t}
        </span>
      ))}
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────
function PostSkeleton() {
  const bar = (w, h = 12, r = 6) => (
    <div style={{ width: w, height: h, borderRadius: r, background: "#EEEEEF" }} />
  )
  return (
    <div style={{
      padding: 16, background: W.cardBg, border: `1px solid ${W.border}`,
      borderRadius: 14,
    }}>
      <div className="wild-shimmer" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ width: 42, height: 42, borderRadius: 21, background: "#EEEEEF" }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, paddingTop: 4 }}>
            {bar(140)}
            {bar(200, 10)}
          </div>
        </div>
        {bar(170, 24, 999)}
        {bar("90%", 14)}
        {bar("100%", 12)}
        {bar("95%", 12)}
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {bar(60, 20, 10)}
          {bar(70, 20, 10)}
          {bar(50, 20, 10)}
        </div>
      </div>
    </div>
  )
}

// ── Share icon ────────────────────────────────────────────────────────────
function ShareIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  )
}

function CommentIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  )
}

// ── Message-boss modal ────────────────────────────────────────────────────
function MessageBossModal({ post, candidateId, onClose }) {
  const [text, setText] = useState("")
  const [sending, setSending] = useState(false)
  const firstName = (post.author.name || "the boss").trim().split(/\s+/)[0]
  const roleLower = (post.chipRole || post.role || "").toLowerCase()

  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = "" }
  }, [])
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose?.() }
    document.addEventListener("keydown", h)
    return () => document.removeEventListener("keydown", h)
  }, [onClose])

  const canSend = text.trim().length > 0 && !sending
  const subtitle = post.author.headline || (post.role ? `hiring · ${post.role}` : "")

  const send = async () => {
    if (!canSend) return
    setSending(true)
    try {
      const res = await fetch(`${API_BASE}/wild/reachouts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          candidateId, bossId: post.bossId, postId: post.id, message: text.trim(),
        }),
      })
      if (res.status !== 201) throw new Error(`HTTP ${res.status}`)
      toast.success(`sent to ${firstName}.`)
      onClose?.()
    } catch (err) {
      console.error("Reach-out failed:", err)
      toast.error("couldn’t send. try again.")
      setSending(false)
    }
  }

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "rgba(15,23,42,0.42)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      fontFamily: FF,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: 520,
        background: W.bg, borderRadius: 20, overflow: "hidden",
        boxShadow: "0 24px 48px rgba(15,23,42,0.18)",
        border: `1px solid ${W.border}`,
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 12, padding: "16px 18px",
          borderBottom: `1px solid ${W.divider}`,
        }}>
          <button onClick={onClose} aria-label="Close" style={{
            width: 32, height: 32, borderRadius: 8, border: "none",
            background: W.frameBg, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", color: W.ink,
          }}>
            <ArrowLeft size={16} />
          </button>
          <Avatar url={post.author.avatar} name={post.author.name || "the boss"} size={38} source={post.source} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 15, fontWeight: 800, color: W.ink, letterSpacing: "-0.02em",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {post.author.name || "the boss"}
            </div>
            {subtitle && (
              <div style={{
                fontSize: 12, color: W.inkMuted, marginTop: 2,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {subtitle}
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: "18px 18px 10px" }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
            color: W.inkFaint, marginBottom: 10, paddingLeft: 2,
          }}>YOUR MESSAGE</div>
          <textarea
            value={text} onChange={e => setText(e.target.value)} disabled={sending}
            maxLength={1000}
            placeholder={`hey ${(firstName || "there").toLowerCase()}, saw your post about ${roleLower}…`}
            style={{
              width: "100%", minHeight: 150, resize: "vertical", padding: 14,
              borderRadius: 14, border: `1px solid ${W.chipBorder}`,
              background: "#fff", color: W.ink,
              fontSize: 15, lineHeight: 1.5, fontFamily: FF, outline: "none",
              boxSizing: "border-box",
            }}
            onFocus={e => e.currentTarget.style.borderColor = W.orange}
            onBlur={e => e.currentTarget.style.borderColor = W.chipBorder}
          />
        </div>
        <div style={{ padding: "6px 18px 18px" }}>
          <button onClick={send} disabled={!canSend} style={{
            width: "100%", height: 50, border: "none", borderRadius: 14,
            background: canSend ? W.cta : "rgba(242,90,32,0.35)",
            color: "#fff", fontSize: 15, fontWeight: 800, fontFamily: FF,
            cursor: canSend ? "pointer" : "not-allowed",
            letterSpacing: "-0.01em",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {sending ? (
              <span className="wild-spinner" style={{
                width: 18, height: 18, borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
              }} />
            ) : "send message"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Hiring card (feed + detail) ───────────────────────────────────────────
function HiringCard({ post, state, expanded, onOpen, onVote, onShare, onMessageBoss }) {
  const displayName = post.author.name || "Anonymous"
  const ago = formatAgo(post.createdAt)
  const cta = post.externalUrl
    ? "message boss"
    : (post.bossId ? "message boss" : "read & reach out")

  return (
    <article
      onClick={expanded ? undefined : onOpen}
      style={{
        position: "relative", padding: "18px 18px 16px",
        background: W.cardBg, border: `1px solid ${W.border}`,
        borderRadius: 16, cursor: expanded ? "default" : "pointer",
        boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
        transition: "transform 0.12s ease, box-shadow 0.15s ease, border-color 0.15s",
        maxWidth: CARD_MAX,
      }}
      onMouseEnter={e => { if (!expanded) {
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(15,23,42,0.05)"
        e.currentTarget.style.borderColor = "#D4D4D8"
      } }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(15,23,42,0.03)"
        e.currentTarget.style.borderColor = W.border
      }}
    >
      {/* Author row */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <Avatar url={post.author.avatar} name={displayName} size={42} source={post.source} />
        <div style={{ flex: 1, minWidth: 0, paddingRight: 24 }}>
          <div style={{
            fontSize: 15, fontWeight: 800, color: W.ink, lineHeight: 1.2,
            fontFamily: FF, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {displayName}
          </div>
          {post.author.headline && (
            <div style={{
              fontSize: 12, color: W.inkMuted, lineHeight: 1.35, marginTop: 3,
              fontFamily: FF,
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}>
              {post.author.headline}
            </div>
          )}
          {ago && (
            <div style={{ fontSize: 11, color: W.inkFaint, marginTop: 4, fontFamily: FF }}>
              {ago}
            </div>
          )}
        </div>
        {!expanded && (
          <div style={{ color: W.inkDim, display: "flex", flexShrink: 0 }}>
            <ArrowUpRight size={18} />
          </div>
        )}
      </div>

      {/* Role chip */}
      {post.chipRole && (
        <div style={{ marginTop: 14 }}>
          <RoleChip role={post.chipRole} company={post.chipCompany} />
        </div>
      )}

      {/* Role name / summary */}
      {post.summary && (
        <div style={{
          marginTop: 12, fontSize: 15, fontWeight: 800,
          color: W.ink, lineHeight: 1.4, fontFamily: FF, letterSpacing: "-0.01em",
          display: expanded ? "block" : "-webkit-box",
          WebkitLineClamp: expanded ? "unset" : 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {post.summary}
        </div>
      )}

      {/* Body */}
      {post.body && (
        <div style={{
          marginTop: 8, whiteSpace: "pre-wrap",
          fontSize: expanded ? 14 : 13,
          color: W.inkMuted, lineHeight: expanded ? 1.6 : 1.5, fontFamily: FF,
          display: expanded ? "block" : "-webkit-box",
          WebkitLineClamp: expanded ? "unset" : 4,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {post.body}
        </div>
      )}

      <TagStrip tags={post.tags} />

      {/* Actions */}
      <div style={{
        marginTop: 14, paddingTop: 14, borderTop: `1px dashed ${W.border}`,
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 2,
          background: W.frameBg, border: `1px solid ${W.border}`, borderRadius: 999,
          padding: "2px 4px",
        }}>
          <button
            aria-label="upvote"
            onClick={() => onVote?.(1)}
            style={{
              width: 26, height: 26, borderRadius: "50%", border: "none",
              background: state.vote === 1 ? W.greenSoft : "transparent",
              color: state.vote === 1 ? W.live : W.inkMuted,
              cursor: "pointer", fontSize: 12, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>▲</button>
          <span style={{ fontSize: 12, fontWeight: 800, color: W.ink, minWidth: 20, textAlign: "center" }}>
            {state.votes}
          </span>
          <button
            aria-label="downvote"
            onClick={() => onVote?.(-1)}
            style={{
              width: 26, height: 26, borderRadius: "50%", border: "none",
              background: state.vote === -1 ? W.peachSoft : "transparent",
              color: state.vote === -1 ? W.cta : W.inkMuted,
              cursor: "pointer", fontSize: 12, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>▼</button>
        </div>

        <button
          onClick={onOpen}
          aria-label="comments"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 10px", height: 30, borderRadius: 999,
            background: "none", border: "none", cursor: "pointer",
            color: W.inkMuted, fontSize: 12, fontWeight: 600, fontFamily: FF,
          }}>
          <CommentIcon /> {state.commentCount}
        </button>

        <button
          onClick={onShare}
          aria-label="share"
          title="share"
          style={{
            width: 32, height: 30, borderRadius: 999, border: "none",
            background: "none", cursor: "pointer", color: W.inkMuted,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
          <ShareIcon />
        </button>

        <div style={{ flex: 1 }} />

        <button
          onClick={onMessageBoss}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "9px 16px", borderRadius: 999, border: "none",
            background: W.cta, color: "#fff",
            fontSize: 13, fontWeight: 700, fontFamily: FF, cursor: "pointer",
            letterSpacing: "-0.01em",
          }}>
          {cta} <span aria-hidden="true">↗</span>
        </button>
      </div>
    </article>
  )
}

// ── Right rail ────────────────────────────────────────────────────────────
function Rail() {
  const block = ({ title, body }) => (
    <div style={{
      padding: "14px 16px", background: W.cardBg,
      border: `1px solid #D4D4D8`, borderRadius: 12,
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
        color: W.ink, textTransform: "uppercase",
        marginBottom: 8, fontFamily: FF,
      }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: W.ink2, lineHeight: 1.6, fontFamily: FF }}>
        {body}
      </div>
    </div>
  )

  return (
    <aside style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {block({
        title: "why this exists",
        body: (
          <>
            <p style={{ margin: 0 }}>
              the best jobs in tech aren’t on job boards. they’re in posts written by
              founders, VPs and team leads who are actually hiring — and most of those
              posts vanish in a day.
            </p>
            <p style={{ margin: "8px 0 0" }}>we collect them here. that’s it.</p>
          </>
        ),
      })}

      {block({
        title: "upvote if",
        body: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>the poster is the <b>actual hiring manager</b> (founder, head, lead)</li>
            <li style={{ marginTop: 4 }}>the role is clear: what, where, what they want</li>
            <li style={{ marginTop: 4 }}>you’d reply if you fit</li>
          </ul>
        ),
      })}

      {block({
        title: "downvote if",
        body: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>recruiter / agency spam</li>
            <li style={{ marginTop: 4 }}>a quote-post of someone else’s role</li>
            <li style={{ marginTop: 4 }}>generic “we’re hiring for 50 roles”</li>
            <li style={{ marginTop: 4 }}>older than two weeks</li>
          </ul>
        ),
      })}

      {block({
        title: "the mission",
        body: (
          <p style={{ margin: 0 }}>
            posts from real bosses on LinkedIn and X are the ones that get you the
            best jobs. this is a community-run feed — your votes decide what rises.
          </p>
        ),
      })}
    </aside>
  )
}

// ── Repost note (inside comments block) ───────────────────────────────────
function RepostNote({ url }) {
  return (
    <div style={{
      padding: "12px 14px", borderRadius: 12,
      background: W.peach, color: "#1A1A1A",
      fontSize: 13, lineHeight: 1.5, fontFamily: FF, marginBottom: 18,
    }}>
      <b>heads up: </b>
      this post was taken from {sourceLabel(sourceOf(url))} — the boss posted it
      there, not here. to apply, open the{" "}
      <a href={url} target="_blank" rel="noreferrer"
         style={{ color: "#1A1A1A", fontWeight: 800, textDecoration: "underline" }}>
        original post ↗
      </a>{" "}
      and reach out to them directly.
    </div>
  )
}

// ── Tal-style comments block ──────────────────────────────────────────────
function TalComments({ postId, state, userId, setState }) {
  const [text, setText] = useState("")
  const [sending, setSending] = useState(false)
  const myHex = anonHex(userId)

  useEffect(() => {
    if (state.comments !== null || state.loadingComments) return
    setState(s => ({ ...s, loadingComments: true, commentsError: null }))
    fetch(`${API_BASE}/wild/posts/${postId}/comments`)
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const body = await r.json()
        const list = (body.comments || []).map(normalizeComment)
        setState(s => ({ ...s, comments: list, commentCount: list.length, loadingComments: false }))
      })
      .catch(err => {
        console.error("Failed to load comments:", err)
        setState(s => ({ ...s, loadingComments: false, commentsError: String(err) }))
      })
  }, [postId])

  const submit = async (e) => {
    e?.preventDefault?.()
    const body = text.trim()
    if (!body || sending) return
    if (!userId) { toast.error("sign in to comment."); return }
    setSending(true)
    try {
      const res = await fetch(`${API_BASE}/wild/posts/${postId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, body }),
      })
      if (res.status !== 201) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const c = normalizeComment(data.comment || {})
      setState(s => ({
        ...s, comments: [...(s.comments || []), c],
        commentCount: (s.commentCount || 0) + 1,
      }))
      setText("")
    } catch (err) {
      console.error("Post comment failed:", err)
      toast.error("couldn’t post your comment. try again.")
    } finally { setSending(false) }
  }

  const comments = state.comments || []
  const count = state.commentCount || 0

  return (
    <section style={{ marginTop: 24 }}>
      <h3 style={{
        margin: "0 0 14px", fontSize: 20, fontWeight: 800,
        color: W.ink, letterSpacing: "-0.02em", fontFamily: FF,
        display: "flex", alignItems: "baseline", gap: 10,
      }}>
        comments
        <small style={{
          fontSize: 12, fontWeight: 600, color: W.inkFaint, letterSpacing: 0,
        }}>
          {count} anon
        </small>
      </h3>

      {state.loadingComments && comments.length === 0 ? (
        <div style={{ padding: "22px 0", display: "flex", justifyContent: "center" }}>
          <span className="wild-spinner" style={{
            width: 20, height: 20, borderRadius: "50%",
            border: `2px solid ${W.divider}`, borderTopColor: W.orange,
          }} />
        </div>
      ) : state.commentsError && comments.length === 0 ? (
        <div style={{ padding: "14px 0", fontSize: 13, color: W.inkFaint, fontFamily: FF }}>
          couldn’t load comments.
        </div>
      ) : comments.length === 0 ? (
        <div style={{ padding: "14px 0", fontSize: 13, color: W.inkFaint, fontFamily: FF }}>
          be the first to comment.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {comments.map((c, i) => {
            const hex = anonHex(c.authorId || c.id || String(i))
            return (
              <div key={c.id || i} style={{ fontFamily: FF }}>
                <div style={{
                  display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
                }}>
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: `#${hex}`,
                    letterSpacing: "-0.01em",
                  }}>
                    anon #{hex}
                  </span>
                  <span style={{ fontSize: 11, color: W.inkFaint }}>
                    {formatAgo(c.createdAt)}
                  </span>
                </div>
                <div style={{
                  marginTop: 4, fontSize: 14, lineHeight: 1.55, color: W.ink2,
                  whiteSpace: "pre-wrap",
                }}>
                  {c.body}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <form onSubmit={submit} style={{
        marginTop: 20, padding: 16, background: W.cardBg,
        border: `1px solid ${W.border}`, borderRadius: 14,
      }}>
        <textarea
          value={text} onChange={e => setText(e.target.value)} disabled={sending}
          maxLength={2000} required
          placeholder="say something useful. or funny. or both."
          style={{
            width: "100%", minHeight: 90, resize: "vertical",
            padding: "12px 14px", borderRadius: 10,
            border: `1px solid ${W.chipBorder}`, background: W.frameBg,
            color: W.ink, fontFamily: FF, fontSize: 14, lineHeight: 1.45,
            outline: "none", boxSizing: "border-box",
          }}
          onFocus={e => e.currentTarget.style.borderColor = W.orange}
          onBlur={e => e.currentTarget.style.borderColor = W.chipBorder}
        />
        <div style={{
          marginTop: 10, display: "flex", alignItems: "center",
          gap: 12, flexWrap: "wrap",
        }}>
          <button
            type="submit"
            disabled={sending || !text.trim()}
            style={{
              padding: "10px 18px", border: "none", borderRadius: 10,
              background: `linear-gradient(135deg, ${W.cta} 0%, ${W.ctaDk} 100%)`,
              color: "#fff",
              cursor: (sending || !text.trim()) ? "not-allowed" : "pointer",
              opacity: (sending || !text.trim()) ? 0.5 : 1,
              fontSize: 13, fontWeight: 800, fontFamily: FF,
              letterSpacing: "-0.005em",
              boxShadow: (sending || !text.trim()) ? "none" : "0 4px 12px rgba(242,90,32,0.28)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              minWidth: 150,
            }}>
            {sending ? (
              <span className="wild-spinner" style={{
                width: 14, height: 14, borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
              }} />
            ) : "post anonymously"}
          </button>
          <small style={{
            fontSize: 12, color: W.inkFaint, fontFamily: FF,
          }}>
            you’ll show as{" "}
            <code style={{
              padding: "1px 6px", borderRadius: 4,
              background: W.frameBg, border: `1px solid ${W.border}`,
              color: `#${myHex}`, fontWeight: 700,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 11,
            }}>
              anon #{myHex}
            </code>
            . be kind.
          </small>
        </div>
      </form>
    </section>
  )
}

// ── Main WildTab component ────────────────────────────────────────────────
export default function WildTab({ userId }) {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [messageTarget, setMessageTarget] = useState(null)
  const [activity, setActivity] = useState({})
  const voteInFlight = useRef(new Set())

  const [sort, setSort] = useState("hot")      // hot | new | top
  const [source, setSource] = useState("all")  // all | linkedin | x
  const [roleFamily, setRoleFamily] = useState("")
  const [seniority, setSeniority] = useState("")
  const [showMore, setShowMore] = useState(false)

  const applyPosts = (list) => {
    const normalized = list.map(classifyPost)
    setPosts(normalized)
    setActivity(prev => {
      const next = {}
      for (const p of normalized) {
        next[p.id] = prev[p.id] || {
          vote: 0, votes: p.votes, commentCount: p.commentCount,
          comments: null, loadingComments: false, commentsError: null,
        }
      }
      return next
    })
  }

  const load = async () => {
    try {
      const res = await fetch(`${API_BASE}/wild/posts?limit=50`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const raw = await res.text()
      const body = JSON.parse(raw)
      applyPosts(body.posts || [])
      setError(null)
      try { localStorage.setItem(POSTS_CACHE_KEY, raw) } catch {}
    } catch (err) {
      console.error("Wild fetch failed:", err)
      if (posts.length === 0) setError(String(err))
    } finally { setLoading(false) }
  }

  useEffect(() => {
    try {
      const raw = localStorage.getItem(POSTS_CACHE_KEY)
      if (raw) {
        const body = JSON.parse(raw)
        if ((body.posts || []).length > 0) {
          applyPosts(body.posts)
          setLoading(false)
        }
      }
    } catch {}
    load()
  }, [])

  // Role-family counts (based on all fetched posts, so chips are stable)
  const familyCounts = useMemo(() => {
    const counts = {}
    for (const p of posts) {
      const f = p.tags.role
      if (f) counts[f] = (counts[f] || 0) + 1
    }
    return counts
  }, [posts])
  const sortedFamilies = useMemo(() => {
    return Object.entries(familyCounts).sort((a, b) => b[1] - a[1])
  }, [familyCounts])

  const seniorityCounts = useMemo(() => {
    const counts = {}
    for (const p of posts) {
      const s = p.tags.seniority
      if (s) counts[s] = (counts[s] || 0) + 1
    }
    return counts
  }, [posts])
  const sortedSeniority = useMemo(() => {
    return Object.entries(seniorityCounts).sort((a, b) => b[1] - a[1])
  }, [seniorityCounts])

  // Apply filters + sort
  const filteredPosts = useMemo(() => {
    let list = posts
    if (source !== "all") list = list.filter(p => p.source === source)
    if (roleFamily) list = list.filter(p => p.tags.role === roleFamily)
    if (seniority) list = list.filter(p => p.tags.seniority === seniority)
    list = [...list]
    if (sort === "new") {
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    } else if (sort === "top") {
      list.sort((a, b) => (activity[b.id]?.votes ?? b.votes) - (activity[a.id]?.votes ?? a.votes))
    } else {
      // hot = recency × votes
      const now = Date.now()
      const score = (p) => {
        const t = p.createdAt ? new Date(p.createdAt).getTime() : now
        const ageH = Math.max(1, (now - t) / 3600000)
        const v = (activity[p.id]?.votes ?? p.votes) + 1
        return v / Math.pow(ageH, 0.5)
      }
      list.sort((a, b) => score(b) - score(a))
    }
    return list
  }, [posts, source, roleFamily, seniority, sort, activity])

  const selectedPost = useMemo(
    () => (selectedId ? posts.find(p => p.id === selectedId) : null),
    [posts, selectedId]
  )

  const setPostState = (postId, updater) => {
    setActivity(prev => {
      const cur = prev[postId] || {
        vote: 0, votes: 0, commentCount: 0, comments: null,
        loadingComments: false, commentsError: null,
      }
      const next = typeof updater === "function" ? updater(cur) : updater
      return { ...prev, [postId]: next }
    })
  }

  const vote = async (post, value) => {
    if (!userId) { toast.error("sign in to vote."); return }
    if (voteInFlight.current.has(post.id)) return
    const cur = activity[post.id]
    if (!cur) return
    const target = cur.vote === value ? 0 : value
    const prevVote = cur.vote
    const prevVotes = cur.votes
    const optimistic = prevVotes - prevVote + target
    setPostState(post.id, s => ({ ...s, vote: target, votes: optimistic }))
    voteInFlight.current.add(post.id)
    try {
      const res = await fetch(`${API_BASE}/wild/posts/${post.id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, value: target }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const updated = Number(data.votes)
      if (Number.isFinite(updated)) setPostState(post.id, s => ({ ...s, votes: updated }))
    } catch (err) {
      console.error("Vote failed:", err)
      setPostState(post.id, s => ({ ...s, vote: prevVote, votes: prevVotes }))
      toast.error("couldn’t register vote.")
    } finally {
      voteInFlight.current.delete(post.id)
    }
  }

  const share = async (post) => {
    const url = post.externalUrl || `${window.location.origin}/dashboard/wild`
    try {
      if (navigator.share) {
        await navigator.share({ title: post.chipRole || post.role || "Hiring post", url })
      } else {
        await navigator.clipboard.writeText(url)
        toast.success("link copied")
      }
    } catch {}
  }

  const messageBoss = (post) => {
    if (post.externalUrl) {
      window.open(post.externalUrl, "_blank", "noopener,noreferrer")
      return
    }
    if (!post.bossId) {
      toast("messaging isn’t available for this post yet.", { icon: "💬" })
      return
    }
    if (!userId) { toast.error("sign in to message the boss."); return }
    setMessageTarget(post)
  }

  // Chip helper
  const Chip = ({ active, onClick, children, title }) => (
    <button onClick={onClick} title={title}
      style={{
        padding: "6px 12px", borderRadius: 999,
        background: active ? W.chipActiveBg : W.chipBg,
        color: active ? W.chipActiveFg : W.ink2,
        border: `1px solid ${active ? W.chipActiveBorder : W.chipBorder}`,
        fontSize: 12, fontWeight: active ? 700 : 600, fontFamily: FF,
        cursor: "pointer", whiteSpace: "nowrap", letterSpacing: "-0.005em",
        display: "inline-flex", alignItems: "center", gap: 5,
      }}>
      {children}
    </button>
  )

  // ── Header (minimal) ─────────────────────────────────────────────────
  const totalLive = posts.length
  const Masthead = (
    <header style={{
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      padding: "2px 2px 16px", marginBottom: 16,
      borderBottom: `1px solid ${W.border}`,
    }}>
      <WildMaskIcon size={22} color={W.cta} />
      <h1 style={{
        margin: 0, fontSize: 22, fontWeight: 800, color: W.ink,
        letterSpacing: "-0.025em", fontFamily: FF, lineHeight: 1,
      }}>
        jobs in the wild
        <span style={{ color: W.cta }}>.</span>
      </h1>
      {totalLive > 0 && (
        <span style={{
          fontSize: 12, fontWeight: 600, color: W.inkDim, fontFamily: FF,
        }}>
          {totalLive} live
        </span>
      )}
      <div style={{ flex: 1 }} />
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 12, fontWeight: 600, color: W.inkMuted, fontFamily: FF,
      }}>
        <span className="wild-live-dot" style={{
          width: 7, height: 7, borderRadius: "50%", background: W.live,
        }} />
        3,519 here now
      </span>
    </header>
  )

  const Hero = !selectedId && (
    <section style={{
      marginBottom: 18,
      display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
    }}>
      <h2 style={{
        margin: 0, fontSize: 18, fontWeight: 700, color: W.ink2,
        letterSpacing: "-0.02em", lineHeight: 1.4, fontFamily: FF,
        flex: 1, minWidth: 240,
      }}>
        upvote the good ones{" "}
        <span style={{ color: W.live, fontWeight: 800 }}>▲</span>
        {"  "}· downvote the spam{" "}
        <span style={{ color: W.cta, fontWeight: 800 }}>▼</span>
      </h2>
      <span style={{
        fontSize: 11, fontWeight: 700, color: W.inkMuted, fontFamily: FF,
        letterSpacing: "0.06em", textTransform: "uppercase",
      }}>
        posts auto-expire in 7 days
      </span>
    </section>
  )

  // ── Filter rows ───────────────────────────────────────────────────────
  const Filters = !selectedId && (
    <div style={{
      display: "flex", flexDirection: "column", gap: 10,
      padding: "12px 14px", background: W.cardBg,
      border: `1px solid ${W.border}`, borderRadius: 14, marginBottom: 18,
    }}>
      {/* Primary row: sort + source + more */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.14em",
          color: W.inkFaint, textTransform: "uppercase", marginRight: 2, fontFamily: FF,
        }}>sort</span>
        {["hot", "new", "top"].map(s => (
          <Chip key={s} active={sort === s} onClick={() => setSort(s)}>{s}</Chip>
        ))}
        <span style={{ width: 1, height: 20, background: W.divider, margin: "0 4px" }} />
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.14em",
          color: W.inkFaint, textTransform: "uppercase", marginRight: 2, fontFamily: FF,
        }}>source</span>
        {[["all","all"],["linkedin","linkedin"],["x","x"]].map(([k, label]) => (
          <Chip key={k} active={source === k} onClick={() => setSource(k)}>{label}</Chip>
        ))}

        <Chip active={showMore} onClick={() => setShowMore(s => !s)}>
          more {showMore ? "▴" : "▾"}
        </Chip>

        <div style={{ flex: 1 }} />
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          fontSize: 11, fontWeight: 600, color: W.inkMuted, fontFamily: FF,
        }}>
          <span className="wild-live-dot" style={{ width: 7, height: 7, borderRadius: "50%", background: W.live }} />
          3,519 live
        </span>
      </div>

      {/* Function row (scrollable chips) */}
      {sortedFamilies.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.14em",
            color: W.inkFaint, textTransform: "uppercase", flexShrink: 0, fontFamily: FF,
          }}>function</span>
          <div className="wild-chip-scroll" style={{
            display: "flex", gap: 6, overflowX: "auto", flex: 1,
            scrollbarWidth: "none", paddingBottom: 2,
          }}>
            <Chip active={!roleFamily} onClick={() => setRoleFamily("")}>all</Chip>
            {sortedFamilies.map(([k, count]) => (
              <Chip key={k} active={roleFamily === k} onClick={() => setRoleFamily(roleFamily === k ? "" : k)}>
                {k} <span style={{ opacity: 0.6, fontWeight: 500 }}>({count})</span>
              </Chip>
            ))}
          </div>
        </div>
      )}

      {/* Secondary row (level) */}
      {showMore && sortedSeniority.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.14em",
            color: W.inkFaint, textTransform: "uppercase", flexShrink: 0, fontFamily: FF,
          }}>level</span>
          <div className="wild-chip-scroll" style={{
            display: "flex", gap: 6, overflowX: "auto", flex: 1,
            scrollbarWidth: "none", paddingBottom: 2,
          }}>
            <Chip active={!seniority} onClick={() => setSeniority("")}>all</Chip>
            {sortedSeniority.map(([k, count]) => (
              <Chip key={k} active={seniority === k} onClick={() => setSeniority(seniority === k ? "" : k)}>
                {k} <span style={{ opacity: 0.6, fontWeight: 500 }}>({count})</span>
              </Chip>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  // Feed body
  let feed
  if (loading && posts.length === 0) {
    feed = (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {Array.from({ length: 4 }).map((_, i) => <PostSkeleton key={i} />)}
      </div>
    )
  } else if (error && posts.length === 0) {
    feed = (
      <div style={{
        padding: "60px 20px", textAlign: "center", background: W.cardBg,
        border: `1px solid ${W.border}`, borderRadius: 14, fontFamily: FF,
      }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: W.ink }}>couldn’t load posts</div>
        <div style={{ fontSize: 13, color: W.inkFaint, marginTop: 6 }}>
          check your connection and try again.
        </div>
        <button onClick={() => { setLoading(true); load() }} style={{
          marginTop: 14, padding: "8px 18px", border: "none", borderRadius: 8,
          background: W.ink, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
        }}>retry</button>
      </div>
    )
  } else if (filteredPosts.length === 0) {
    feed = (
      <div style={{
        padding: "60px 20px", textAlign: "center", background: W.cardBg,
        border: `1px solid ${W.border}`, borderRadius: 14, fontFamily: FF,
      }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: W.ink }}>nothing matches these filters</div>
        <div style={{ fontSize: 13, color: W.inkFaint, marginTop: 6 }}>
          try widening the sort or clearing a chip.
        </div>
      </div>
    )
  } else {
    feed = (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {filteredPosts.map(p => {
          const st = activity[p.id] || { vote: 0, votes: p.votes, commentCount: p.commentCount }
          return (
            <HiringCard
              key={p.id} post={p} state={st}
              onOpen={() => setSelectedId(p.id)}
              onVote={v => vote(p, v)}
              onShare={() => share(p)}
              onMessageBoss={() => messageBoss(p)}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div style={{ padding: "4px 0 32px", fontFamily: FF }}>
      <style>{`
        @keyframes wild-shimmer-anim { 0% { opacity: 0.55; } 50% { opacity: 0.9; } 100% { opacity: 0.55; } }
        .wild-shimmer > div > * { animation: wild-shimmer-anim 1.4s ease-in-out infinite; }
        @keyframes wild-spin { to { transform: rotate(360deg); } }
        .wild-spinner { animation: wild-spin 0.75s linear infinite; display: inline-block; }
        @keyframes wild-live-pulse {
          0% { box-shadow: 0 0 0 0 rgba(23, 163, 74, 0.5); }
          70% { box-shadow: 0 0 0 7px rgba(23, 163, 74, 0); }
          100% { box-shadow: 0 0 0 0 rgba(23, 163, 74, 0); }
        }
        .wild-live-dot { animation: wild-live-pulse 1.4s ease-out infinite; }
        .wild-chip-scroll::-webkit-scrollbar { display: none; }
        .wild-layout {
          display: grid;
          grid-template-columns: minmax(0, ${CARD_MAX}px) 300px;
          gap: 24px;
          align-items: start;
          justify-content: start;
        }
        @media (max-width: 1024px) {
          .wild-layout { grid-template-columns: minmax(0, 1fr); justify-content: stretch; }
          .mobile-tag { display: inline !important; }
          .desktop-tag { display: none !important; }
        }
      `}</style>

      {selectedId && selectedPost ? (
        (() => {
          const st = activity[selectedPost.id] || {
            vote: 0, votes: selectedPost.votes,
            commentCount: selectedPost.commentCount, comments: null,
          }
          return (
            <>
              <button
                onClick={() => setSelectedId(null)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: "none", border: "none", padding: "4px 2px",
                  color: W.inkMuted, fontSize: 13, fontWeight: 600, fontFamily: FF,
                  cursor: "pointer", marginBottom: 14,
                }}>
                ← back to the feed
              </button>
              <div style={{ maxWidth: CARD_MAX, marginLeft: 0, marginRight: "auto" }}>
                <HiringCard
                  post={selectedPost} state={st} expanded
                  onVote={v => vote(selectedPost, v)}
                  onShare={() => share(selectedPost)}
                  onMessageBoss={() => messageBoss(selectedPost)}
                />
                <div style={{ marginTop: 10 }}>
                  {selectedPost.externalUrl && (
                    <RepostNote url={selectedPost.externalUrl} />
                  )}
                  <TalComments
                    postId={selectedPost.id} state={st}
                    userId={userId}
                    setState={updater => setPostState(selectedPost.id, updater)}
                  />
                </div>
              </div>
            </>
          )
        })()
      ) : selectedId && !selectedPost ? (
        <div style={{ padding: 20, textAlign: "center", color: W.inkFaint, fontFamily: FF }}>
          post not found. <button onClick={() => setSelectedId(null)} style={{
            background: "none", border: "none", color: W.orange, cursor: "pointer", fontWeight: 700,
          }}>back to feed</button>
        </div>
      ) : (
        <>
          {Masthead}
          {Hero}
          {Filters}
          <div className="wild-layout">
            <main>{feed}</main>
            <Rail />
          </div>
        </>
      )}

      {messageTarget && (
        <MessageBossModal
          post={messageTarget}
          candidateId={userId}
          onClose={() => setMessageTarget(null)}
        />
      )}
    </div>
  )
}
